import React, { useState, useEffect } from 'react'
import AuthForm from './components/AuthForm'
import CustomerSelector from './components/CustomerSelector'
import ProductSelector from './components/ProductSelector'
import DocumentGenerator from './components/DocumentGenerator'
import PriceManager from './components/PriceManager'
import { getSheetData } from './services/sheets'

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0])
  const [documentType, setDocumentType] = useState('price-info')
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('form')

  useEffect(() => {
    if (isAuthenticated) {
      loadProducts()
    }
  }, [isAuthenticated])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const data = await getSheetData()
      
      // Apply custom prices from localStorage
      const customPrices = localStorage.getItem('customPrices')
      if (customPrices) {
        const priceMap = JSON.parse(customPrices)
        const updatedData = data.map(product => ({
          ...product,
          price: priceMap[product.id] !== undefined ? priceMap[product.id] : product.price
        }))
        setProducts(updatedData)
      } else {
        setProducts(data)
      }
    } catch (err) {
      setError('Failed to load products. Using demo data.')
      // Use demo data if API fails
      const demoProducts = [
        { id: 'prod_a_std', name: 'Product A', category: 'Main', variant: 'Standard', price: 299, description: 'Basic analytics suite' },
        { id: 'prod_a_ent', name: 'Product A', category: 'Main', variant: 'Enterprise', price: 599, description: 'Advanced analytics with AI' },
        { id: 'prod_b_std', name: 'Product B', category: 'Main', variant: 'Standard', price: 399, description: 'Cloud platform starter' },
        { id: 'prod_b_ent', name: 'Product B', category: 'Main', variant: 'Enterprise', price: 799, description: 'Cloud platform pro' },
        { id: 'prod_c_std', name: 'Product C', category: 'Main', variant: 'Standard', price: 199, description: 'Integration hub basic' },
        { id: 'prod_c_ent', name: 'Product C', category: 'Main', variant: 'Enterprise', price: 499, description: 'Integration hub advanced' },
        { id: 'ext_backup', name: 'Backup Service', category: 'External', price: 99, description: 'Automated daily backups' },
        { id: 'ext_monitor', name: 'Monitoring', category: 'External', price: 149, description: '24/7 system monitoring' },
        { id: 'ext_support', name: 'Premium Support', category: 'External', price: 299, description: 'Dedicated support team' },
        { id: 'ext_training', name: 'Training Package', category: 'External', price: 199, description: 'Monthly training sessions' },
        { id: 'ext_custom', name: 'Custom Integration', category: 'External', price: 399, description: 'Custom API development' }
      ]
      
      // Apply custom prices to demo data too
      const customPrices = localStorage.getItem('customPrices')
      if (customPrices) {
        const priceMap = JSON.parse(customPrices)
        const updatedDemo = demoProducts.map(product => ({
          ...product,
          price: priceMap[product.id] !== undefined ? priceMap[product.id] : product.price
        }))
        setProducts(updatedDemo)
      } else {
        setProducts(demoProducts)
      }
    } finally {
      setLoading(false)
    }
  }

  const calculateTotal = () => {
    const total = selectedProducts.reduce((sum, productId) => {
      const product = products.find(p => p.id === productId)
      return sum + (product?.price || 0)
    }, 0)

    // Apply bundle discount if more than one product selected
    if (selectedProducts.length > 1) {
      return {
        total: total * 0.9,
        originalTotal: total,
        hasDiscount: true
      }
    }
    return { total, originalTotal: total, hasDiscount: false }
  }

  const handlePriceUpdate = (newPrices) => {
    // Update products with new prices
    const updatedProducts = products.map(product => ({
      ...product,
      price: newPrices[product.id] !== undefined ? newPrices[product.id] : product.price
    }))
    setProducts(updatedProducts)
  }

  if (!isAuthenticated) {
    return <AuthForm onAuthenticate={() => setIsAuthenticated(true)} />
  }

  const pricing = calculateTotal()

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Proposal Generator</h1>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('form')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'form'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Proposal Form
              </button>
              <button
                onClick={() => setActiveTab('prices')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'prices'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Price Management
              </button>
            </nav>
          </div>
          
          {error && activeTab === 'form' && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded text-yellow-800">
              {error}
            </div>
          )}

          {activeTab === 'form' ? (
            <div className="space-y-8">
            {/* Customer Selection */}
            <CustomerSelector
              selectedAccount={selectedAccount}
              selectedContact={selectedContact}
              onAccountChange={setSelectedAccount}
              onContactChange={setSelectedContact}
            />

            {/* Document Settings */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Date
                </label>
                <input
                  type="date"
                  value={documentDate}
                  onChange={(e) => setDocumentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="price-info">Price Information</option>
                  <option value="contract">Contract Document</option>
                </select>
              </div>
            </div>

            {/* Product Selection */}
            <ProductSelector
              products={products}
              selectedProducts={selectedProducts}
              onProductChange={setSelectedProducts}
              loading={loading}
            />

            {/* Pricing Summary */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Pricing Summary</h3>
              <div className="space-y-2">
                {selectedProducts.map(productId => {
                  const product = products.find(p => p.id === productId)
                  return product ? (
                    <div key={productId} className="flex justify-between text-gray-700">
                      <span>{product.name} {product.variant ? `(${product.variant})` : ''}</span>
                      <span>€{product.price}/month</span>
                    </div>
                  ) : null
                })}
                {pricing.hasDiscount && (
                  <>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between text-gray-700">
                        <span>Subtotal</span>
                        <span>€{pricing.originalTotal}/month</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-semibold">
                        <span>Bundle Discount (10%)</span>
                        <span>-€{(pricing.originalTotal - pricing.total).toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>Total</span>
                    <span>€{pricing.total.toFixed(2)}/month</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Document */}
            <DocumentGenerator
              documentType={documentType}
              selectedAccount={selectedAccount}
              selectedContact={selectedContact}
              selectedProducts={selectedProducts}
              products={products}
              documentDate={documentDate}
              pricing={pricing}
              disabled={!selectedAccount || selectedProducts.length === 0}
            />
            </div>
          ) : (
            <PriceManager products={products} onPriceUpdate={handlePriceUpdate} />
          )}
        </div>
      </div>
    </div>
  )
}

export default App